/**
 * Modo Treino (item 5.2): tutorial, desafios por posição e campo livre com bots. `#treino` abre a escolha;
 * `#treino?tutorial` e `#treino?desafio=paredao` abrem direto.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { BOT_LEVELS, BOT_LEVEL_PARAMS, POSITIONS, type BotLevel, type Position } from "@besenliga/shared";
import { browserStore, loadRoster } from "../creator/roster";
import { ControlsHelp, Feed, Minimap, PilotPanel, useFlightView } from "../flight/Hud";
import { clubsFor, pilotFrom } from "../flight/pilot";
import type { ViewOptions } from "../flight/view";
import {
  CHALLENGES,
  MEDAL_LABEL,
  createChallenge,
  loadRecords,
  medalFor,
  saveRecord,
  type ChallengeId,
} from "./challenges";
import type { Scenario, ScenarioHud } from "./scenario";
import { TUTORIAL_LESSONS, Tutorial } from "./tutorial";

export const TUTORIAL_KEY = "besenliga.tutorial.v1";

/** Melhor tempo do tutorial (s), ou null se nunca terminou. */
export function tutorialBest(store: Pick<Storage, "getItem"> | null): number | null {
  try {
    const v = Number(JSON.parse(store?.getItem(TUTORIAL_KEY) ?? "null")?.bestSeconds);
    return Number.isFinite(v) && v > 0 ? v : null;
  } catch {
    return null;
  }
}

function saveTutorial(seconds: number): void {
  const store = browserStore();
  const best = tutorialBest(store);
  if (best !== null && best <= seconds) return;
  try {
    store?.setItem(TUTORIAL_KEY, JSON.stringify({ bestSeconds: Math.round(seconds) }));
  } catch {
    /* sem armazenamento: só não guarda */
  }
}

type Run = { kind: "tutorial" } | { kind: "desafio"; id: ChallengeId };

function runFromHash(): Run | null {
  const q = new URLSearchParams(location.hash.split("?")[1] ?? "");
  if (q.has("tutorial")) return { kind: "tutorial" };
  const id = q.get("desafio");
  return CHALLENGES.some((c) => c.id === id) ? { kind: "desafio", id: id as ChallengeId } : null;
}

const mmss = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

export function TrainingScreen() {
  const [run, setRun] = useState<Run | null>(runFromHash);
  const [round, setRound] = useState(0);
  if (run)
    return (
      <TrainingRun
        key={`${run.kind}-${"id" in run ? run.id : ""}-${round}`}
        run={run}
        onAgain={() => setRound((r) => r + 1)}
        onExit={() => setRun(null)}
      />
    );
  return <TrainingHub onStart={setRun} />;
}

function TrainingHub({ onStart }: { onStart: (r: Run) => void }) {
  const records = useMemo(() => loadRecords(browserStore()), []);
  const best = useMemo(() => tutorialBest(browserStore()), []);
  const saved = useMemo(() => loadRoster(browserStore())[0], []);
  const [role, setRole] = useState<Position>(saved?.position ?? "artilheiro");
  const [bots, setBots] = useState<BotLevel | "parados">("medio");
  return (
    <main className="menu-screen">
      <section className="menu-card wide" aria-labelledby="treino-titulo">
        <header className="menu-head">
          <a href="#" className="ghost">
            Menu
          </a>
          <h1 id="treino-titulo">Treino</h1>
        </header>

        <div className="training-tutorial">
          <div>
            <h2>Tutorial</h2>
            <p>
              {TUTORIAL_LESSONS.length} etapas curtas: voar, subir e descer, turbo, pegar a Pelota, passe,
              pedir a bola, arremesso e habilidade. Leva uns 3 minutos.
            </p>
            {best !== null && <p className="training-best">Seu melhor tempo: {mmss(best)}</p>}
          </div>
          <button type="button" className="primary" onClick={() => onStart({ kind: "tutorial" })}>
            {best === null ? "Começar o tutorial" : "Fazer de novo"}
          </button>
        </div>

        <h2 className="training-section">Desafios por posição</h2>
        <div className="training-challenges">
          {CHALLENGES.map((c) => {
            const rec = records[c.id];
            const medal = rec === undefined ? null : medalFor(c, rec);
            return (
              <article key={c.id} className="training-challenge" data-medal={medal ?? "nenhuma"}>
                <p className="training-role">{POSITIONS[c.role].label}</p>
                <h3>{c.title}</h3>
                <p>{c.text}</p>
                <p className="training-goals">
                  Bronze {c.medals[0]} · Prata {c.medals[1]} · Ouro {c.medals[2]} {c.unit}
                </p>
                <p className="training-best">
                  {rec === undefined
                    ? "Ainda não jogado"
                    : `Recorde: ${rec} ${c.unit}${medal ? ` (${MEDAL_LABEL[medal]})` : ""}`}
                </p>
                <button
                  type="button"
                  className="ghost"
                  onClick={() => onStart({ kind: "desafio", id: c.id })}
                >
                  Jogar
                </button>
              </article>
            );
          })}
        </div>

        <h2 className="training-section">Campo livre</h2>
        <p>Voe à vontade, sem regras nem relógio, com os outros 13 parados ou jogando como bots.</p>
        <div className="training-free">
          <div className="segmented" role="group" aria-label="Posição no campo livre">
            {(["artilheiro", "batedor", "cacador", "goleiro"] as const).map((r) => (
              <button key={r} type="button" aria-pressed={role === r} onClick={() => setRole(r)}>
                {POSITIONS[r].label}
              </button>
            ))}
          </div>
          <div className="segmented" role="group" aria-label="Bots no campo livre">
            {(["parados", ...BOT_LEVELS] as const).map((b) => (
              <button key={b} type="button" aria-pressed={bots === b} onClick={() => setBots(b)}>
                {b === "parados" ? "Parados" : BOT_LEVEL_PARAMS[b].label}
              </button>
            ))}
          </div>
          <a className="primary" href={`#voo?posicao=${role}${bots === "parados" ? "" : `&bots=${bots}`}`}>
            Ir para o campo
          </a>
        </div>
      </section>
    </main>
  );
}

function TrainingRun({ run, onAgain, onExit }: { run: Run; onAgain: () => void; onExit: () => void }) {
  const holderRef = useRef<HTMLDivElement>(null);
  const info = run.kind === "desafio" ? CHALLENGES.find((c) => c.id === run.id) : undefined;
  const role: Position = info?.role ?? "artilheiro";
  const saved = useMemo(() => loadRoster(browserStore())[0], []);
  const pilot = useMemo(() => pilotFrom(saved, role), [saved, role]);
  const clubs = useMemo(() => clubsFor(pilot.kitId), [pilot.kitId]);
  const kits = useMemo(() => ({ casa: clubs.casa.kit, fora: clubs.fora.kit }), [clubs]);
  const scenario: Scenario = useMemo(
    () => (run.kind === "tutorial" ? new Tutorial() : createChallenge(run.id)),
    [run],
  );
  const opts: ViewOptions = useMemo(() => ({ mode: "treino", clubs, scenario }), [clubs, scenario]);
  const { state, hud, view, scenario: sh } = useFlightView(holderRef, pilot, opts);
  const [record, setRecord] = useState(false);
  const done = sh?.done ?? false;

  // Fim: guarda o tempo do tutorial ou o recorde do desafio, e para a simulação.
  useEffect(() => {
    if (!done || !sh) return;
    if (view) view.paused = true;
    if (run.kind === "tutorial") saveTutorial(sh.seconds);
    else {
      setRecord(saveRecord(browserStore(), run.id, sh.score ?? 0));
    }
  }, [done]);

  return (
    <div className="stadium-screen flight-screen" data-state={state}>
      <div ref={holderRef} className="stadium-holder" />
      <header className="stadium-bar">
        <button type="button" className="ghost" onClick={onExit}>
          Sair
        </button>
        <h1>{run.kind === "tutorial" ? "Tutorial" : (info?.title ?? "Desafio")}</h1>
      </header>
      <Minimap view={view} kits={kits} />
      {hud && <Feed hud={hud} />}
      {sh && !done && (
        <ScenarioCard sh={sh} onSkip={run.kind === "tutorial" ? () => view?.skipStep() : undefined} />
      )}
      {sh && done && (
        <div className="flight-banner" role="status" data-phase="fim">
          <strong>{sh.title}</strong>
          {sh.summary && <span>{sh.summary}</span>}
          {record && <span className="training-record">Novo recorde!</span>}
          <span className="training-actions">
            <button type="button" className="primary" onClick={onAgain}>
              De novo
            </button>
            <button type="button" className="ghost" onClick={onExit}>
              Voltar ao Treino
            </button>
            {run.kind === "tutorial" && (
              <a className="ghost" href="#partida">
                Jogar uma partida
              </a>
            )}
          </span>
        </div>
      )}
      {hud && <PilotPanel hud={hud} name={pilot.name} role={role} mode="treino" />}
      <ControlsHelp />
      {state === "erro" && (
        <p className="stage-message" role="alert">
          Não foi possível mostrar o treino em 3D neste navegador.
        </p>
      )}
    </div>
  );
}

function ScenarioCard({ sh, onSkip }: { sh: ScenarioHud; onSkip?: (() => void) | undefined }) {
  return (
    <section className="scenario-card" aria-live="polite" aria-label="Objetivo">
      <p className="scenario-progress">
        <span>{sh.progress}</span>
        <span className="scenario-clock">{mmss(sh.countdown ? Math.ceil(sh.seconds) : sh.seconds)}</span>
      </p>
      <h2>{sh.title}</h2>
      <p>{sh.text}</p>
      {onSkip && (
        <button type="button" className="ghost" onClick={onSkip}>
          Pular etapa
        </button>
      )}
    </section>
  );
}
